import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import {
  MiniMenuPublicCategory,
  MiniMenuPublicContent,
  MiniMenuPublicImageSet,
  MiniMenuPublicLanguage,
  MiniMenuPublicModifierGroup,
  MiniMenuPublicModifierOption,
  MiniMenuPublicModifierSet,
  MiniMenuPublicModifiers,
  MiniMenuPublicProduct,
  MiniMenuPublicPromotion,
  MiniMenuPublicTranslation
} from '../models/mini-menu-public.model';

/**
 * Ordering is switched off for the public MiniMenu: no add (+) control, no quantity
 * stepper and no cart ribbon. Evolution's own `orderingAvailable` flag is still read
 * and ANDed with this, so turning ordering back on is a one-line change here once the
 * order path is ready.
 */
export const MINI_MENU_ORDERING_ENABLED = false;

/**
 * Promotion lifecycle. What's On is authored by the restaurant itself, so a promotion
 * can never publish itself: `review` is the Evolution approval queue — the same queue
 * the first business publish lands in — and only an approver moves it to `live`.
 * `promoted` is the paid/house-promoted slot: reserved now, sold later.
 * `suspended` and `deleted` are the two outcomes of acting on a published promotion.
 */
export type MiniMenuPromoStatus = 'draft' | 'review' | 'live' | 'promoted' | 'suspended' | 'deleted';
export type MiniMenuLanguage = 'en' | 'zh-CN';
export type MiniMenuMediaRole = 1 | 2 | 3 | 4;
export type MiniMenuMenuEntityType = 1 | 2;

// One crop rectangle in source-image pixels (cropperjs getData shape). The eventual
// backend maps this to the MiniMenu {cx, cy, scale} crop record (POS spec §8a).
export interface MiniMenuCropRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// The What's On detail image is portrait in both tablet orientations, so a promo
// carries three device crops matching the MiniMenu promo frame ratios
// (mobile 2:3, tablet-landscape 2:3, tablet-portrait 3:8).
export interface MiniMenuPromoCrops {
  mobile: MiniMenuCropRect | null;
  tablet: MiniMenuCropRect | null;
  tabletPortrait: MiniMenuCropRect | null;
}

export interface MiniMenuPromo {
  id: string;
  /** Evolution's managed Foodie News key. 0 until the first save assigns one. */
  promotionId: number;
  badge: string;
  title: string;
  when: string;
  lead: string;
  body: string;
  status: MiniMenuPromoStatus;
  image: string | null;
  mediaId?: number;
  /** Managed media retained so the admin UI can request a private draft preview. */
  mediaAsset?: MiniMenuMediaAsset | null;
  pendingImageFile?: File | null;
  crops: MiniMenuPromoCrops;
  sortOrder: number;
  rowVersion?: string | null;
  /** Older office-authored Foodie News rows remain visible but cannot be edited here. */
  isLegacy?: boolean;
  publishFrom?: string | null;
  publishTo?: string | null;
  /** When the promotion entered the approval queue — gates the re-submit prompt. */
  submittedOn?: string | null;
  publishedOn?: string | null;
  /** Suspension start. MINI_MENU_PROMO_RULES.suspendDays later a decision is forced. */
  suspendedOn?: string | null;
  /** Soft-delete start: restorable for a few days, then purged by Evolution. */
  deletedOn?: string | null;
}

/**
 * Retention and re-submit windows for What's On. These are product rules rather than
 * layout, so both the settings list and the promo modal read them from here.
 */
export const MINI_MENU_PROMO_RULES = {
  /** A promotion stuck in review can be re-submitted after this many working days. */
  resubmitAfterWorkingDays: 3,
  /** A suspended promotion is hidden from the public menu for this long. */
  suspendDays: 7,
  /** A deleted promotion can still be restored within this many days. */
  deleteRestoreDays: 3,
  /** Evolution purges the deleted promotion permanently at this age, to save space. */
  deletePurgeDays: 10
};

/** Evolution stores the promotion status as an int. Kept in one place, both directions. */
const PROMO_STATUS_CODES: Record<MiniMenuPromoStatus, number> = {
  draft: 1,
  live: 2,
  review: 3,
  promoted: 4,
  suspended: 5,
  deleted: 6
};

export function promoStatusCode(status: MiniMenuPromoStatus): number {
  return PROMO_STATUS_CODES[status] || PROMO_STATUS_CODES.draft;
}

// Code 3 used to mean "scheduled". Scheduling is retired, and a scheduled promotion was
// never public, so a legacy 3 reads back as "pending review" rather than as published.
export function promoStatusFromCode(code: number | null | undefined): MiniMenuPromoStatus {
  const match = (Object.keys(PROMO_STATUS_CODES) as MiniMenuPromoStatus[])
    .find((status) => PROMO_STATUS_CODES[status] === Number(code));
  return match || 'draft';
}

/** Whole days elapsed between two instants, floored — used for the retention windows. */
export function calendarDaysSince(value: string | null | undefined, now = new Date()): number {
  const started = value ? new Date(value) : null;
  if (!started || Number.isNaN(started.getTime())) {
    return 0;
  }
  return Math.max(0, Math.floor((now.getTime() - started.getTime()) / 86400000));
}

/**
 * Working days elapsed, counting Mon–Fri only. The approval SLA is quoted in working
 * days, so a Friday submission must not offer a re-submit on the following Monday.
 */
export function workingDaysSince(value: string | null | undefined, now = new Date()): number {
  const started = value ? new Date(value) : null;
  if (!started || Number.isNaN(started.getTime())) {
    return 0;
  }
  const cursor = new Date(started.getFullYear(), started.getMonth(), started.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let days = 0;
  while (cursor < end) {
    cursor.setDate(cursor.getDate() + 1);
    const weekday = cursor.getDay();
    if (weekday !== 0 && weekday !== 6) {
      days++;
    }
  }
  return days;
}

/** One crop frame the editor offers: a device pane and the aspect ratio it renders at. */
export interface MiniMenuCropFrame {
  key: keyof MiniMenuPromoCrops;
  label: string;
  ratio: number;
}

// Aspect ratios (width / height) mirroring MENU_IMAGE_FRAME_RATIOS in the MiniMenu model
// (src/app/models/menu-site.model.ts). Both panes are portrait in BOTH tablet orientations, so
// each frame set carries a landscape-device and a portrait-device crop. The promo detail pane is
// the panel's left half; the landing brand pane is the wider left column — so the two sets have
// DIFFERENT ratios and must not be interchanged.
export const MINI_MENU_PROMO_FRAMES: MiniMenuCropFrame[] = [
  { key: 'mobile', label: 'Mobile', ratio: 2 / 3 },
  { key: 'tablet', label: 'Tablet Horizontal', ratio: 2 / 3 },
  { key: 'tabletPortrait', label: 'Tablet Vertical', ratio: 3 / 8 }
];

/** MENU_IMAGE_FRAME_RATIOS.landing — the landing brand pane, one image, three crops. */
export const MINI_MENU_LANDING_FRAMES: MiniMenuCropFrame[] = [
  { key: 'mobile', label: 'Mobile', ratio: 9 / 16 },
  { key: 'tablet', label: 'Tablet Horizontal', ratio: 5 / 8 },
  { key: 'tabletPortrait', label: 'Tablet Vertical', ratio: 1 / 3 }
];

export function emptyPromoCrops(): MiniMenuPromoCrops {
  return { mobile: null, tablet: null, tabletPortrait: null };
}

export interface MiniMenuTheme {
  font: string;
  backgroundColor: string;
  uiColor: string;
  contentColor: string;
  accentColor: string;
  landingLogoUrl: string | null;
  bannerLogoUrl: string | null;
}

export interface MiniMenuMediaAsset {
  id: number;
  foodieNewsId: number | null;
  role: MiniMenuMediaRole;
  sourceUrl: string;
  mobileUrl: string;
  tabletUrl: string;
  tabletPortraitUrl: string;
  /** Authenticated preview routes used while the site or promotion is still a draft. */
  adminSourceUrl?: string | null;
  adminMobileUrl?: string | null;
  adminTabletUrl?: string | null;
  adminTabletPortraitUrl?: string | null;
  altText: string | null;
  originalWidth: number | null;
  originalHeight: number | null;
  mobileCrop: MiniMenuCropRect | null;
  tabletCrop: MiniMenuCropRect | null;
  tabletPortraitCrop: MiniMenuCropRect | null;
}

export interface MiniMenuPresentation {
  locationTag: string;
  eyebrow: string;
  subline: string;
  font: string;
  backgroundColor: string;
  uiColor: string;
  contentColor: string;
  accentColor: string;
  rowVersion: string | null;
  landingBackground: MiniMenuMediaAsset | null;
  landingLogo: MiniMenuMediaAsset | null;
  bannerLogo: MiniMenuMediaAsset | null;
}

export interface MiniMenuLanguageOption {
  code: string;
  title: string;
  shortTitle: string;
  sortOrder: number;
}

export interface MiniMenuSiteText {
  locationTag: string;
  eyebrow: string;
  subline: string;
}

export interface MiniMenuMenuText {
  entityType: MiniMenuMenuEntityType;
  entityId: number;
  name: string;
  description: string;
}

export interface MiniMenuFoodieNewsText {
  foodieNewsId: number;
  badge: string;
  title: string;
  availabilityText: string;
  lead: string;
  body: string;
}

/** Translated copy for one restaurant-authored promotion. */
export interface MiniMenuPromoText {
  promotionId: number;
  badge: string;
  title: string;
  availabilityText: string;
  lead: string;
  body: string;
}

export interface MiniMenuTranslationBundle {
  languageCode: string;
  site: MiniMenuSiteText;
  menu: MiniMenuMenuText[];
  promotions: MiniMenuPromoText[];
  /** Office-sourced Foodie News copy. Not editable from the restaurant's MiniMenu form. */
  foodieNews: MiniMenuFoodieNewsText[];
}

export interface MiniMenuFoodieNewsSource {
  id: number;
  title: string;
  description: string;
  image: string | null;
  dateTo: string | null;
}

export interface MiniMenuMenuProductSource {
  id: number;
  categoryId: number;
  title: string;
  description: string;
}

export interface MiniMenuMenuCategorySource {
  id: number;
  title: string;
  products: MiniMenuMenuProductSource[];
}

export interface MiniMenuFoodieNewsPresentation {
  foodieNewsId: number;
  title: string;
  body: string;
  baseImage: string | null;
  badge: string;
  availabilityText: string;
  lead: string;
  status: number;
  submittedOn: string | null;
  publishedOn: string | null;
  suspendedOn: string | null;
  deletedOn: string | null;
  reviewNotes: string | null;
  rowVersion: string | null;
  isLegacy: boolean;
  publishFrom: string | null;
  publishTo: string | null;
  sortOrder: number;
  media: MiniMenuMediaAsset | null;
}

/**
 * A restaurant-authored What's On promotion as Evolution stores it. Deliberately not the
 * Foodie News shape: Foodie News is office-sourced editorial the restaurant cannot pick
 * from, while a promotion belongs to the business and carries its own approval state.
 */
export interface MiniMenuPromotionRecord {
  promotionId: number;
  title: string;
  body: string;
  baseImage: string | null;
  badge: string;
  availabilityText: string;
  lead: string;
  status: number;
  publishFrom: string | null;
  publishTo: string | null;
  submittedOn: string | null;
  publishedOn: string | null;
  suspendedOn: string | null;
  deletedOn: string | null;
  rowVersion?: string | null;
  isLegacy?: boolean;
  sortOrder: number;
  media: MiniMenuMediaAsset | null;
}

export interface MiniMenuSettings {
  enabled: boolean;
  locationTag: string;
  eyebrow: string;
  subline: string;
  teaserBackgroundUrl: string | null;
  theme: MiniMenuTheme;
  defaultLanguage: MiniMenuLanguage;
  chineseEnabled: boolean;
  promos: MiniMenuPromo[];
}

export interface MiniMenuEntitlement {
  available: boolean;
  plan: string;
}

export interface MiniMenuBranchOption {
  id: number;
  title: string;
}

export interface MiniMenuSiteAdmin {
  isConfigured: boolean;
  miniMenuSiteId: number | null;
  businessPartnerId: number;
  businessName: string;
  availableBranches: MiniMenuBranchOption[];
  publishedBranchId: number | null;
  slug: string | null;
  isAvailable: boolean;
  isPublished: boolean;
  defaultLanguage: string;
  publicPath: string | null;
  orderingAvailable: boolean;
  orderingMessage: string;
  presentation: MiniMenuPresentation;
  languages: MiniMenuLanguageOption[];
  translations: MiniMenuTranslationBundle[];
  /** Restaurant-authored What's On. Absent until Evolution ships the promotions routes. */
  promotions?: MiniMenuPromotionRecord[];
  /**
   * Legacy What's On rows that were still Foodie News presentations. Read-only fallback so
   * an already-configured site keeps rendering its promotions on the older API.
   */
  foodieNews: MiniMenuFoodieNewsPresentation[];
  menu: MiniMenuMenuCategorySource[];
}

export interface MiniMenuSiteUpdate {
  publishedBranchId: number;
  slug: string;
  isPublished: boolean;
  defaultLanguage: string;
}

export interface MiniMenuPresentationUpdate {
  locationTag: string;
  eyebrow: string;
  subline: string;
  font: string;
  backgroundColor: string;
  uiColor: string;
  contentColor: string;
  accentColor: string;
  rowVersion: string | null;
}

/**
 * CSS stack for each theme font. Shared so the public MiniMenu and the appearance preview
 * cannot drift apart on what a given font id actually renders as.
 */
export const MINI_MENU_FONT_STACKS: Record<string, string> = {
  hanken: "'Hanken Grotesk', system-ui, sans-serif",
  caveat: "'Caveat', cursive",
  playfair: "'Playfair Display', serif",
  bebas: "'Bebas Neue', sans-serif",
  fraunces: "'Fraunces', serif",
  archivo: "'Archivo', sans-serif",
  libre: "'Libre Baskerville', serif"
};

export function miniMenuFontStack(font: string | null | undefined): string {
  return MINI_MENU_FONT_STACKS[String(font || '')] || MINI_MENU_FONT_STACKS['hanken'];
}

const MINI_MENU_FONT_LINK_ID = 'mini-menu-fonts';
const MINI_MENU_FONT_HREF = 'https://fonts.googleapis.com/css2'
  + '?family=Archivo:wght@300;400;500'
  + '&family=Bebas+Neue'
  + '&family=Caveat:wght@400;500;600'
  + '&family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500'
  + '&family=Hanken+Grotesk:wght@300;400;500;700;800'
  + '&family=IBM+Plex+Mono:wght@400;500;600;700'
  + '&family=Libre+Baskerville:wght@400;700'
  + '&family=Playfair+Display:wght@400;500'
  + '&display=swap';

/**
 * Adds the MiniMenu webfont stylesheet once per document. Angular strips `@import url(...)`
 * from component stylesheets, so a theme-font preview renders in system fonts without this.
 * Idempotent by element id.
 */
export function ensureMiniMenuThemeFonts(doc: Document): void {
  if (doc.getElementById(MINI_MENU_FONT_LINK_ID)) {
    return;
  }
  const link = doc.createElement('link');
  link.id = MINI_MENU_FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = MINI_MENU_FONT_HREF;
  doc.head.appendChild(link);
}

export const MINI_MENU_FONTS: Array<{ id: string; title: string }> = [
  { id: 'hanken', title: 'Hanken Grotesk (default)' },
  { id: 'caveat', title: 'Caveat' },
  { id: 'playfair', title: 'Playfair Display' },
  { id: 'bebas', title: 'Bebas Neue' },
  { id: 'fraunces', title: 'Fraunces' },
  { id: 'archivo', title: 'Archivo' },
  { id: 'libre', title: 'Libre Baskerville' }
];

export const DEFAULT_MINI_MENU_SETTINGS: MiniMenuSettings = {
  enabled: false,
  locationTag: '',
  eyebrow: '',
  subline: '',
  teaserBackgroundUrl: null,
  theme: {
    font: 'hanken',
    backgroundColor: '#ECECEA',
    uiColor: '#001E46',
    contentColor: '#16242B',
    accentColor: '#887A57',
    landingLogoUrl: null,
    bannerLogoUrl: null
  },
  defaultLanguage: 'en',
  chineseEnabled: false,
  promos: []
};

interface MiniMenuFallbackConfig {
  slug: string;
  businessPartnerId: number;
  branchId: number;
  businessName: string;
}

interface EvolutionMiniMenuMedia {
  id?: number;
  sourceUrl?: string;
  mobileUrl?: string;
  tabletUrl?: string;
  tabletPortraitUrl?: string;
  altText?: string;
}

interface EvolutionMiniMenuPublicSite {
  slug: string;
  defaultLanguage?: string;
  language?: string;
  orderingAvailable?: boolean;
  orderingMessage?: string;
  presentation?: {
    locationTag?: string;
    eyebrow?: string;
    subline?: string;
    font?: string;
    backgroundColor?: string;
    uiColor?: string;
    contentColor?: string;
    accentColor?: string;
    landingBackground?: EvolutionMiniMenuMedia | null;
    landingLogo?: EvolutionMiniMenuMedia | null;
    bannerLogo?: EvolutionMiniMenuMedia | null;
  };
  languages?: Array<{
    code: string;
    title?: string;
    shortTitle?: string;
    sortOrder?: number;
  }>;
  translations?: Array<{
    languageCode: string;
    site?: {
      locationTag?: string;
      eyebrow?: string;
      subline?: string;
    };
    menu?: Array<{
      entityType: number;
      entityId: number;
      name?: string;
      description?: string;
    }>;
    promotions?: Array<{
      promotionId: number;
      badge?: string;
      title?: string;
      availabilityText?: string;
      lead?: string;
      body?: string;
    }>;
    foodieNews?: Array<{
      foodieNewsId: number;
      badge?: string;
      title?: string;
      availabilityText?: string;
      lead?: string;
      body?: string;
    }>;
  }>;
  promotions?: Array<{
    promotionId: number;
    title?: string;
    body?: string;
    baseImage?: string;
    badge?: string;
    availabilityText?: string;
    lead?: string;
    sortOrder?: number;
    media?: EvolutionMiniMenuMedia | null;
  }>;
  foodieNews?: Array<{
    foodieNewsId: number;
    title?: string;
    body?: string;
    baseImage?: string;
    badge?: string;
    availabilityText?: string;
    lead?: string;
    sortOrder?: number;
    media?: EvolutionMiniMenuMedia | null;
  }>;
  business: {
    businessPartnerId: number;
    branchId: number;
    name?: string;
    description?: string;
    businessMessage?: string;
    sourceCurrency?: string;
  };
  usesStagingFallback?: boolean;
}

interface EvolutionMiniMenuCategory {
  id: number;
  title: string;
}

interface EvolutionMiniMenuModifier {
  id: number;
  name: string;
  price: number;
}

interface EvolutionMiniMenuModifierGroup {
  id: number;
  title: string;
  posModifierList?: EvolutionMiniMenuModifier[];
}

interface EvolutionMiniMenuModifierSet {
  id: number;
  title: string;
  posGroupModifierList?: EvolutionMiniMenuModifierGroup[];
}

interface EvolutionMiniMenuProduct {
  id: number;
  title?: string;
  alternateTitle?: string;
  description?: string;
  price: number;
  productCategoryId?: number | null;
  productCategory?: {
    id: number;
    title: string;
  } | null;
  image?: string;
  imageList?: string[];
  singleModifierList?: EvolutionMiniMenuModifier[];
  groupModifierList?: EvolutionMiniMenuModifierGroup[];
  setModifierList?: EvolutionMiniMenuModifierSet[];
}

interface EvolutionMiniMenuResponse {
  total: number;
  records: EvolutionMiniMenuProduct[];
}

/**
 * Evolution-owned MiniMenu administration contract for the current business.
 *
 * The HUNGERY frontend never persists a parallel local MiniMenu settings copy.
 */
@Injectable({
  providedIn: 'root'
})
export class MiniMenuService {
  // Local key for promotions Evolution has not numbered yet, so *ngFor and the modal
  // can still tell two unsaved drafts apart.
  private newPromoSeq = 0;

  constructor(private http: HttpClient) {}

  getPublicMenu(slug: string): Observable<MiniMenuPublicContent> {
    const normalizedSlug = String(slug || '').trim().toLowerCase();
    return this.resolvePublicSite(normalizedSlug).pipe(
      switchMap((site) => forkJoin({
        categories: this.http.get<EvolutionMiniMenuCategory[]>(
          this.apiUrl(
            `api/tenants/membershipApp/GetAllMembershipProductCategories?businessPartnerId=${site.business.businessPartnerId}`
          )
        ).pipe(
          // Products also carry category identity. A supporting category lookup
          // must not hide an otherwise usable public menu.
          catchError(() => of([] as EvolutionMiniMenuCategory[]))
        ),
        menu: this.http.get<EvolutionMiniMenuResponse>(
          this.apiUrl(
            `api/tenants/membershipApp/GetCompanyMenue?SubBranchId=${site.business.branchId}&SkipPaging=true`
          )
        )
      }).pipe(
        map(({ categories, menu }) => this.mapPublicContent(
          site,
          categories,
          Array.isArray(menu?.records) ? menu.records : []
        ))
      ))
    );
  }

  getSite(businessPartnerId: number): Observable<MiniMenuSiteAdmin> {
    return this.http.get<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/site`
    );
  }

  updateSite(businessPartnerId: number, record: MiniMenuSiteUpdate): Observable<MiniMenuSiteAdmin> {
    return this.http.put<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/site`,
      record
    );
  }

  updatePresentation(
    businessPartnerId: number,
    record: MiniMenuPresentationUpdate
  ): Observable<MiniMenuSiteAdmin> {
    return this.http.put<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/presentation`,
      record
    );
  }

  updateLanguages(
    businessPartnerId: number,
    defaultLanguage: string,
    languages: MiniMenuLanguageOption[]
  ): Observable<MiniMenuSiteAdmin> {
    return this.http.put<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/languages`,
      { defaultLanguage, languages }
    );
  }

  updateFoodieNews(
    businessPartnerId: number,
    items: MiniMenuFoodieNewsPresentation[]
  ): Observable<MiniMenuSiteAdmin> {
    return this.http.put<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/foodie-news`,
      { items }
    );
  }

  /**
   * The deployed Evolution API stores restaurant-authored What's On drafts as managed
   * Foodie News records. Create the draft before uploading role-4 media so the API has
   * an owner to validate and attach the image to.
   */
  createFoodieNews(
    businessPartnerId: number,
    promo: MiniMenuPromo,
    index = 0
  ): Observable<MiniMenuFoodieNewsPresentation> {
    return this.http.post<MiniMenuFoodieNewsPresentation>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/foodie-news`,
      this.toFoodieNewsSave(promo, index)
    );
  }

  saveFoodieNews(
    businessPartnerId: number,
    promo: MiniMenuPromo,
    index = 0
  ): Observable<MiniMenuFoodieNewsPresentation> {
    if (!promo.promotionId) {
      return this.createFoodieNews(businessPartnerId, promo, index);
    }
    return this.http.put<MiniMenuFoodieNewsPresentation>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/foodie-news/${promo.promotionId}`,
      this.toFoodieNewsSave(promo, index)
    );
  }

  updateFoodieNewsOrder(businessPartnerId: number, items: MiniMenuPromo[]): Observable<MiniMenuSiteAdmin> {
    return this.http.put<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/foodie-news/order`,
      {
        items: items.filter((item) => item.promotionId && !item.isLegacy).map((item, index) => ({
          foodieNewsId: item.promotionId,
          sortOrder: item.sortOrder || (index + 1) * 10,
          rowVersion: item.rowVersion
        }))
      }
    );
  }

  submitFoodieNews(businessPartnerId: number, promo: MiniMenuPromo): Observable<MiniMenuSiteAdmin> {
    return this.foodieNewsAction(businessPartnerId, promo, 'submit');
  }

  resubmitFoodieNews(businessPartnerId: number, promo: MiniMenuPromo): Observable<MiniMenuSiteAdmin> {
    return this.foodieNewsAction(businessPartnerId, promo, 'resubmit');
  }

  suspendFoodieNews(businessPartnerId: number, promo: MiniMenuPromo): Observable<MiniMenuSiteAdmin> {
    return this.foodieNewsAction(businessPartnerId, promo, 'suspend');
  }

  restoreFoodieNews(businessPartnerId: number, promo: MiniMenuPromo): Observable<MiniMenuSiteAdmin> {
    return this.foodieNewsAction(businessPartnerId, promo, 'restore');
  }

  deleteFoodieNews(businessPartnerId: number, promo: MiniMenuPromo): Observable<MiniMenuSiteAdmin> {
    return this.http.delete<MiniMenuFoodieNewsPresentation>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/foodie-news/${promo.promotionId}`
      + `?rowVersion=${encodeURIComponent(promo.rowVersion || '')}`
    ).pipe(switchMap(() => this.getSite(businessPartnerId)));
  }

  private foodieNewsAction(
    businessPartnerId: number,
    promo: MiniMenuPromo,
    action: 'submit' | 'resubmit' | 'suspend' | 'restore'
  ): Observable<MiniMenuSiteAdmin> {
    return this.http.post<MiniMenuFoodieNewsPresentation>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/foodie-news/${promo.promotionId}/${action}`,
      { rowVersion: promo.rowVersion }
    ).pipe(switchMap(() => this.getSite(businessPartnerId)));
  }

  /**
   * Saves the restaurant's own What's On promotions. Drafts included — saving never
   * publishes, exactly like Save Details on the business form.
   */
  updatePromotions(
    businessPartnerId: number,
    items: MiniMenuPromotionRecord[]
  ): Observable<MiniMenuSiteAdmin> {
    return this.http.put<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/promotions`,
      { items }
    );
  }

  /**
   * Hands one promotion to the Evolution approval queue. Mirrors the first business
   * publish (BlissController `api/bliss/business/publish`): the caller does not choose
   * the resulting state — the ERP marks it pending approval, and only an approver makes
   * it visible on the directory site.
   */
  publishPromotion(businessPartnerId: number, promotionId: number): Observable<MiniMenuSiteAdmin> {
    return this.http.post<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/promotions/${promotionId}/publish`,
      {}
    );
  }

  /** Puts a stale pending-review promotion back at the front of the approval queue. */
  resubmitPromotion(businessPartnerId: number, promotionId: number): Observable<MiniMenuSiteAdmin> {
    return this.http.post<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/promotions/${promotionId}/resubmit`,
      {}
    );
  }

  /** Hides a published promotion from the public menu for MINI_MENU_PROMO_RULES.suspendDays. */
  suspendPromotion(businessPartnerId: number, promotionId: number): Observable<MiniMenuSiteAdmin> {
    return this.http.post<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/promotions/${promotionId}/suspend`,
      {}
    );
  }

  /** Returns a suspended or recently deleted promotion to the approval queue. */
  restorePromotion(businessPartnerId: number, promotionId: number): Observable<MiniMenuSiteAdmin> {
    return this.http.post<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/promotions/${promotionId}/restore`,
      {}
    );
  }

  /** Soft-delete. Evolution keeps the row for the restore window, then purges it. */
  deletePromotion(businessPartnerId: number, promotionId: number): Observable<MiniMenuSiteAdmin> {
    return this.http.delete<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/promotions/${promotionId}`
    );
  }

  updateTranslations(
    businessPartnerId: number,
    translation: MiniMenuTranslationBundle
  ): Observable<MiniMenuSiteAdmin> {
    return this.http.put<MiniMenuSiteAdmin>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/translations`,
      translation
    );
  }

  /** Role 4 is the What's On image, keyed by its managed Foodie News record. */
  uploadMedia(
    businessPartnerId: number,
    role: MiniMenuMediaRole,
    file: File,
    foodieNewsId?: number
  ): Observable<MiniMenuMediaAsset> {
    const body = new FormData();
    body.append('file', file, file.name);
    const ownerQuery = foodieNewsId ? `&foodieNewsId=${foodieNewsId}` : '';
    return this.http.post<MiniMenuMediaAsset>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/media?role=${role}${ownerQuery}`,
      body
    );
  }

  updateMediaCrop(
    businessPartnerId: number,
    mediaId: number,
    crops: MiniMenuPromoCrops,
    altText = ''
  ): Observable<MiniMenuMediaAsset> {
    return this.http.put<MiniMenuMediaAsset>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/media/${mediaId}/crop`,
      {
        altText,
        mobileCrop: crops.mobile,
        tabletCrop: crops.tablet,
        tabletPortraitCrop: crops.tabletPortrait
      }
    );
  }

  deleteMedia(businessPartnerId: number, mediaId: number): Observable<void> {
    return this.http.delete<void>(
      `${environment.apiRoot}api/minimenu/admin/vendors/${businessPartnerId}/media/${mediaId}`
    );
  }

  /**
   * Loads a managed image through Evolution's authenticated admin route. Draft media
   * deliberately returns 404 from the public route, so settings previews must use this
   * request (and the auth interceptor) instead of assigning the public URL to an <img>.
   */
  getAdminMediaPreview(
    businessPartnerId: number,
    asset: MiniMenuMediaAsset
  ): Observable<Blob> {
    const configuredUrl = String(asset.adminSourceUrl || '').trim();
    const path = configuredUrl
      || `api/minimenu/admin/vendors/${businessPartnerId}/media/${asset.id}`;
    const url = /^(https?:|data:)/i.test(path) ? path : this.apiUrl(path);
    return this.http.get(url, { responseType: 'blob' });
  }

  /** A blank restaurant-authored promotion. Starts as a draft — nothing is submitted yet. */
  createPromo(): MiniMenuPromo {
    this.newPromoSeq++;
    return {
      id: `promo-new-${this.newPromoSeq}`,
      promotionId: 0,
      badge: '',
      title: '',
      when: '',
      lead: '',
      body: '',
      status: 'draft',
      image: null,
      crops: emptyPromoCrops(),
      sortOrder: 0,
      publishFrom: null,
      publishTo: null,
      submittedOn: null,
      publishedOn: null,
      suspendedOn: null,
      deletedOn: null
    };
  }

  /** Flattens a promo back to the Evolution promotion shape for the promotions PUT. */
  toPromotionRecord(promo: MiniMenuPromo, index: number): MiniMenuPromotionRecord {
    return {
      promotionId: promo.promotionId || 0,
      title: promo.title,
      body: promo.body,
      baseImage: promo.image,
      badge: promo.badge,
      availabilityText: promo.when,
      lead: promo.lead,
      status: promoStatusCode(promo.status),
      publishFrom: promo.publishFrom || null,
      publishTo: promo.publishTo || null,
      submittedOn: promo.submittedOn || null,
      publishedOn: promo.publishedOn || null,
      suspendedOn: promo.suspendedOn || null,
      deletedOn: promo.deletedOn || null,
      sortOrder: promo.sortOrder || (index + 1) * 10,
      media: null
    };
  }

  toFoodieNewsSave(promo: MiniMenuPromo, index: number): object {
    return {
      title: promo.title,
      body: promo.body,
      badge: promo.badge,
      availabilityText: promo.when,
      lead: promo.lead,
      sortOrder: promo.sortOrder || (index + 1) * 10,
      rowVersion: promo.rowVersion || null
    };
  }

  settingsFromSite(site: MiniMenuSiteAdmin): MiniMenuSettings {
    const fallback = this.clone(DEFAULT_MINI_MENU_SETTINGS);
    const presentation = site.presentation || {} as MiniMenuPresentation;
    return {
      ...fallback,
      enabled: site.isPublished,
      locationTag: String(presentation.locationTag || '').slice(0, 80),
      eyebrow: presentation.eyebrow || '',
      subline: presentation.subline || '',
      teaserBackgroundUrl: presentation.landingBackground ? null : this.mediaUrl(presentation.landingBackground),
      theme: {
        font: presentation.font || fallback.theme.font,
        backgroundColor: presentation.backgroundColor || fallback.theme.backgroundColor,
        uiColor: presentation.uiColor || fallback.theme.uiColor,
        contentColor: presentation.contentColor || fallback.theme.contentColor,
        accentColor: presentation.accentColor || fallback.theme.accentColor,
        landingLogoUrl: presentation.landingLogo ? null : this.mediaUrl(presentation.landingLogo),
        bannerLogoUrl: presentation.bannerLogo ? null : this.mediaUrl(presentation.bannerLogo)
      },
      defaultLanguage: site.defaultLanguage === 'zh-CN' ? 'zh-CN' : 'en',
      chineseEnabled: (site.languages || []).some((language) => language.code === 'zh-CN'),
      promos: this.promoRecords(site).map((promo, index) => ({
          id: String(promo.promotionId),
          promotionId: promo.promotionId,
          badge: promo.badge || '',
          title: promo.title || '',
          when: promo.availabilityText || '',
          lead: promo.lead || '',
          body: promo.body || '',
          status: promoStatusFromCode(promo.status),
          // A draft's public media URL intentionally returns 404. The settings component
          // replaces this with an authenticated blob URL after loading the admin asset.
          image: promo.media ? null : (promo.baseImage || null),
          mediaId: promo.media?.id,
          mediaAsset: promo.media || null,
          crops: {
            mobile: promo.media?.mobileCrop || null,
            tablet: promo.media?.tabletCrop || null,
            tabletPortrait: promo.media?.tabletPortraitCrop || null
          },
          sortOrder: promo.sortOrder || (index + 1) * 10,
          publishFrom: promo.publishFrom || null,
          publishTo: promo.publishTo || null,
          submittedOn: promo.submittedOn || null,
          publishedOn: promo.publishedOn || null,
          suspendedOn: promo.suspendedOn || null,
          deletedOn: promo.deletedOn || null,
          rowVersion: promo.rowVersion || null,
          isLegacy: Boolean(promo.isLegacy)
        }))
    };
  }

  /**
   * Promotions come from `promotions` once Evolution ships those routes. Until then an
   * already-configured site still returns its What's On as Foodie News presentations, so
   * those are read (never written) under the promotion shape to keep the list populated.
   */
  private promoRecords(site: MiniMenuSiteAdmin): MiniMenuPromotionRecord[] {
    if (site.promotions?.length) {
      return site.promotions;
    }
    return (site.foodieNews || []).map((record) => ({
      promotionId: record.foodieNewsId,
      title: record.title,
      body: record.body,
      baseImage: record.baseImage,
      badge: record.badge,
      availabilityText: record.availabilityText,
      lead: record.lead,
      status: record.status,
      publishFrom: record.publishFrom,
      publishTo: record.publishTo,
      submittedOn: record.submittedOn || null,
      publishedOn: record.publishedOn || null,
      suspendedOn: record.suspendedOn || null,
      deletedOn: record.deletedOn || null,
      rowVersion: record.rowVersion || null,
      isLegacy: Boolean(record.isLegacy),
      sortOrder: record.sortOrder,
      media: record.media
    }));
  }

  private resolvePublicSite(slug: string): Observable<EvolutionMiniMenuPublicSite> {
    return this.http.get<EvolutionMiniMenuPublicSite>(
      this.apiUrl(`api/minimenu/site/${encodeURIComponent(slug)}`)
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        const fallback = environment.miniMenuFallback as MiniMenuFallbackConfig | null;
        if (error.status === 404 && fallback && fallback.slug.toLowerCase() === slug) {
          return of(this.stagingFallbackSite(fallback));
        }
        return throwError(() => error);
      })
    );
  }

  private stagingFallbackSite(fallback: MiniMenuFallbackConfig): EvolutionMiniMenuPublicSite {
    return {
      slug: fallback.slug,
      defaultLanguage: 'en',
      language: 'en',
      orderingAvailable: false,
      orderingMessage: 'Online ordering is coming soon.',
      presentation: {
        locationTag: fallback.businessName,
        eyebrow: 'Digital menu',
        subline: 'Browse our live Evolution menu. Online ordering is coming soon.',
        font: 'hanken',
        backgroundColor: '#ECECEA',
        uiColor: '#001E46',
        contentColor: '#16242B',
        accentColor: '#887A57'
      },
      languages: [
        { code: 'en', title: 'English', shortTitle: 'EN', sortOrder: 10 }
      ],
      translations: [],
      foodieNews: [],
      business: {
        businessPartnerId: fallback.businessPartnerId,
        branchId: fallback.branchId,
        name: fallback.businessName,
        description: 'HUNGERY Cafe',
        businessMessage: 'Browse our live Evolution menu.',
        sourceCurrency: 'Rp'
      },
      usesStagingFallback: true
    };
  }

  private mapPublicContent(
    site: EvolutionMiniMenuPublicSite,
    categoryRecords: EvolutionMiniMenuCategory[],
    productRecords: EvolutionMiniMenuProduct[]
  ): MiniMenuPublicContent {
    const categories = this.mapPublicCategories(categoryRecords, productRecords);
    const businessName = site.business?.name || 'HUNGERY';
    const defaultLanguage = this.languageCode(site.language || site.defaultLanguage);
    const languages = (site.languages || [])
      .map((language) => ({
        code: this.languageCode(language.code),
        label: language.title || language.code,
        shortLabel: language.shortTitle || language.code.toUpperCase()
      }))
      .filter((language, index, items) =>
        items.findIndex((candidate) => candidate.code === language.code) === index);

    return {
      loadState: 'ready',
      slug: site.slug,
      businessName,
      businessPageUrl: `${String(environment.hungeryPublicRoot).replace(/\/+$/, '')}/business-detail/${site.business.branchId}`,
      location: site.presentation?.locationTag || businessName,
      eyebrow: site.presentation?.eyebrow || 'Digital menu',
      subline: site.presentation?.subline
        || site.business?.businessMessage
        || site.business?.description
        || site.orderingMessage
        || 'Browse the menu. Online ordering is coming soon.',
      currency: site.business?.sourceCurrency || 'Rp',
      orderingAvailable: MINI_MENU_ORDERING_ENABLED && Boolean(site.orderingAvailable),
      theme: {
        font: site.presentation?.font || 'hanken',
        backgroundColor: site.presentation?.backgroundColor || '#ECECEA',
        uiColor: site.presentation?.uiColor || '#001E46',
        contentColor: site.presentation?.contentColor || '#16242B',
        accentColor: site.presentation?.accentColor || '#887A57',
        landingLogoUrl: this.evolutionMediaUrl(site.presentation?.landingLogo?.sourceUrl),
        bannerLogoUrl: this.evolutionMediaUrl(site.presentation?.bannerLogo?.sourceUrl),
        landingBackground: this.publicImageSet(site.presentation?.landingBackground)
      },
      languages: languages.length
        ? languages
        : [{ code: defaultLanguage, label: defaultLanguage === 'zh-CN' ? 'Chinese' : 'English', shortLabel: defaultLanguage === 'zh-CN' ? 'ZH.CN' : 'EN' }],
      defaultLanguage,
      translations: this.mapPublicTranslations(site, categories),
      categories,
      promotions: this.mapPublicPromotions(site),
      usesStagingFallback: Boolean(site.usesStagingFallback)
    };
  }

  private mapPublicCategories(
    categoryRecords: EvolutionMiniMenuCategory[],
    productRecords: EvolutionMiniMenuProduct[]
  ): MiniMenuPublicCategory[] {
    const categories = new Map<number, MiniMenuPublicCategory>();

    (Array.isArray(categoryRecords) ? categoryRecords : []).forEach((record) => {
      const id = Number(record?.id);
      if (!Number.isFinite(id)) {
        return;
      }
      categories.set(id, {
        id: String(id),
        name: record.title || 'Menu',
        products: []
      });
    });

    (Array.isArray(productRecords) ? productRecords : []).forEach((record) => {
      const productId = Number(record?.id);
      if (!Number.isFinite(productId)) {
        return;
      }

      const categoryId = Number(record.productCategoryId || record.productCategory?.id || 0);
      if (!categories.has(categoryId)) {
        categories.set(categoryId, {
          id: String(categoryId),
          name: record.productCategory?.title || 'Menu',
          products: []
        });
      }

      categories.get(categoryId)?.products.push(this.mapPublicProduct(record, categoryId));
    });

    return Array.from(categories.values());
  }

  private mapPublicProduct(
    record: EvolutionMiniMenuProduct,
    categoryId: number
  ): MiniMenuPublicProduct {
    const images = [
      record.image,
      ...(Array.isArray(record.imageList) ? record.imageList : [])
    ]
      .map((image) => String(image || '').trim())
      .filter((image, index, values) => Boolean(image) && values.indexOf(image) === index);

    // The three Evolution modifier shapes stay distinct. Flattening them into one list
    // loses the only thing that tells the menu how to render each: a loose single has no
    // group title to print, and a set is an authoring bundle whose groups must read as
    // ordinary groups rather than inheriting a "Set - Group" compound name.
    const modifiers: MiniMenuPublicModifiers = {
      single: this.mapPublicModifierOptions(record.singleModifierList),
      groups: (Array.isArray(record.groupModifierList) ? record.groupModifierList : [])
        .map((group, index) => this.mapPublicModifierGroup(
          group,
          `product-${record.id}-group-${group?.id || index}`
        ))
        .filter((group) => group.options.length),
      sets: (Array.isArray(record.setModifierList) ? record.setModifierList : [])
        .map((set, setIndex): MiniMenuPublicModifierSet => ({
          id: `product-${record.id}-set-${set?.id || setIndex}`,
          name: set?.title || '',
          groups: (Array.isArray(set?.posGroupModifierList) ? set.posGroupModifierList : [])
            .map((group, groupIndex) => this.mapPublicModifierGroup(
              group,
              `product-${record.id}-set-${set?.id || setIndex}-group-${group?.id || groupIndex}`
            ))
            .filter((group) => group.options.length)
        }))
        .filter((set) => set.groups.length)
    };

    return {
      id: String(record.id),
      categoryId: String(categoryId),
      name: record.title || record.alternateTitle || 'Menu item',
      description: record.description || '',
      price: this.toPublicPrice(record.price),
      images,
      modifiers
    };
  }

  private mapPublicModifierGroup(
    group: EvolutionMiniMenuModifierGroup,
    id: string
  ): MiniMenuPublicModifierGroup {
    return {
      id,
      // An untitled group still needs a label, but it must not be invented per-set —
      // the menu falls back to a neutral heading only when Evolution sent no title.
      name: String(group?.title || '').trim(),
      options: this.mapPublicModifierOptions(group?.posModifierList)
    };
  }

  private mapPublicModifierOptions(
    records: EvolutionMiniMenuModifier[] | undefined
  ): MiniMenuPublicModifierOption[] {
    return (Array.isArray(records) ? records : [])
      .filter((record) => record && Number.isFinite(Number(record.id)))
      .map((record) => ({
        id: String(record.id),
        name: record.name || 'Modifier',
        priceDelta: this.toPublicPrice(record.price)
      }));
  }

  /**
   * What's On is the restaurant's own promotions. Only approved ones reach this endpoint,
   * so nothing is filtered here; the Foodie News arm is the pre-promotions fallback.
   */
  private mapPublicPromotions(site: EvolutionMiniMenuPublicSite): MiniMenuPublicPromotion[] {
    const records = site.promotions?.length
      ? site.promotions.map((promotion) => ({ ...promotion, key: promotion.promotionId }))
      : (site.foodieNews || []).map((promotion) => ({ ...promotion, key: promotion.foodieNewsId }));

    return records
      .map((promotion, index) => ({
        id: String(promotion.key),
        badge: promotion.badge || '',
        title: promotion.title || '',
        when: promotion.availabilityText || '',
        lead: promotion.lead || '',
        body: String(promotion.body || '')
          .split(/\n{1,}/)
          .map((paragraph) => paragraph.trim())
          .filter(Boolean),
        image: this.publicImageSet(promotion.media, promotion.baseImage),
        sortOrder: promotion.sortOrder || (index + 1) * 10
      }))
      .sort((left, right) => left.sortOrder - right.sortOrder);
  }

  /**
   * Evolution renders each upload into mobile / tablet / tablet-portrait crops. Keep all
   * three so the layout can pick the one matching the pane it is painting; `baseImage`
   * (or the uncropped source) is the last resort when a crop has not been generated.
   */
  private publicImageSet(
    media: EvolutionMiniMenuMedia | null | undefined,
    fallbackUrl?: string | null
  ): MiniMenuPublicImageSet | null {
    const source = this.evolutionMediaUrl(media?.sourceUrl || fallbackUrl);
    const mobile = this.evolutionMediaUrl(media?.mobileUrl) || source;
    const tablet = this.evolutionMediaUrl(media?.tabletUrl) || source;
    const tabletPortrait = this.evolutionMediaUrl(media?.tabletPortraitUrl) || tablet;

    if (!mobile && !tablet && !tabletPortrait) {
      return null;
    }
    return { mobile, tablet, tabletPortrait };
  }

  private mapPublicTranslations(
    site: EvolutionMiniMenuPublicSite,
    categories: MiniMenuPublicCategory[]
  ): Record<string, MiniMenuPublicTranslation> {
    const translations: Record<string, MiniMenuPublicTranslation> = {};

    (site.translations || [])
      .filter((bundle) => bundle?.languageCode && this.languageCode(bundle.languageCode) !== 'en')
      .forEach((bundle) => {
        const translation: MiniMenuPublicTranslation = {
          teaser: {
            location: bundle.site?.locationTag || '',
            eyebrow: bundle.site?.eyebrow || '',
            subline: bundle.site?.subline || ''
          },
          categories: {},
          promotions: {}
        };

        (bundle.menu || []).forEach((record) => {
          if (record.entityType === 1) {
            const categoryId = String(record.entityId);
            translation.categories![categoryId] = translation.categories![categoryId] || { products: {} };
            translation.categories![categoryId].name = record.name || '';
            return;
          }
          if (record.entityType !== 2) {
            return;
          }

          const productId = String(record.entityId);
          const category = categories.find((candidate) =>
            candidate.products.some((product) => product.id === productId));
          if (!category) {
            return;
          }
          translation.categories![category.id] = translation.categories![category.id] || { products: {} };
          translation.categories![category.id].products = translation.categories![category.id].products || {};
          translation.categories![category.id].products![productId] = {
            name: record.name || '',
            description: record.description || ''
          };
        });

        const promotionText = bundle.promotions?.length
          ? bundle.promotions.map((record) => ({ ...record, key: record.promotionId }))
          : (bundle.foodieNews || []).map((record) => ({ ...record, key: record.foodieNewsId }));

        promotionText.forEach((record) => {
          translation.promotions![String(record.key)] = {
            badge: record.badge || '',
            title: record.title || '',
            when: record.availabilityText || '',
            lead: record.lead || '',
            body: String(record.body || '')
              .split(/\n{1,}/)
              .map((paragraph) => paragraph.trim())
              .filter(Boolean)
          };
        });

        translations[this.languageCode(bundle.languageCode)] = translation;
      });

    return translations;
  }

  private languageCode(value: string | null | undefined): MiniMenuPublicLanguage {
    const normalized = String(value || '').toLowerCase();
    return normalized === 'zh-cn' || normalized === 'zh-tw' ? 'zh-CN' : 'en';
  }

  private toPublicPrice(value: number | string | null | undefined): number {
    const price = Number(value);
    return Number.isFinite(price) ? price : 0;
  }

  private apiUrl(path: string): string {
    return `${String(environment.apiRoot || '').replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
  }

  private evolutionMediaUrl(value?: string | null): string | null {
    const normalized = String(value || '').trim();
    if (!normalized) {
      return null;
    }
    if (/^(https?:|data:)/i.test(normalized)) {
      return normalized;
    }
    return this.apiUrl(normalized);
  }

  publicMediaUrl(asset: MiniMenuMediaAsset | null | undefined): string | null {
    return this.mediaUrl(asset);
  }

  private mediaUrl(asset: MiniMenuMediaAsset | null | undefined): string | null {
    const value = asset?.sourceUrl || '';
    if (!value) {
      return null;
    }
    if (/^https?:\/\//i.test(value) || value.startsWith('data:')) {
      return value;
    }
    return `${String(environment.apiRoot || '').replace(/\/+$/, '')}/${value.replace(/^\/+/, '')}`;
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value)) as T;
  }
}
